import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bell, BellOff } from "lucide-react";
import { useAuth } from "@/app/auth";
import { api } from "@/api/client";
import { useWorkspace } from "@/api/workspace";
import { disablePush, enablePush, syncPush, usePushState } from "./push";
import { inboxTotal, readPushBinding, setBadge } from "./pushStore";
import type { InboxCounts } from "./pushStore";

export function PwaPushSettings() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const state = usePushState();
    return <section className="pm-pwa-push">
        {state.enabled ? <Bell size={25} aria-hidden="true" /> : <BellOff size={25} aria-hidden="true" />}
        <div><strong>{t("pwa.push.title")}</strong>
            <p>{t(state.enabled ? "pwa.push.enabled" : "pwa.push.hint")}</p>
            {state.error && <p role="alert">{t(`pwa.push.${state.error}`)}</p>}
            {!state.serverEnabled && <p role="status">{t("pwa.push.adminDisabled")}</p>}
        </div>
        <button type="button" disabled={state.busy || !user || (!state.serverEnabled && !state.enabled)}
            onClick={() => { if (state.enabled) void disablePush(); else if (user) void enablePush(user.id); }}>
            {t(state.busy ? "pwa.push.busy" : state.enabled ? "pwa.push.disable" : "pwa.push.enable")}
        </button>
    </section>;
}

export function PwaPushBridge() {
    const { user, loading } = useAuth();
    const workspace = useWorkspace();
    const queries = useQueryClient();
    const state = usePushState();
    useEffect(() => {
        if (user && !loading) void syncPush(user.id);
    }, [user?.id, user?.company?.id, loading, workspace]);
    const counts = useQuery<{ data: InboxCounts }>({
        queryKey: ["inbox", "counts"], queryFn: () => api.raw<InboxCounts>("/inbox"),
        enabled: Boolean(user && state.enabled), staleTime: 30_000,
        refetchInterval: state.enabled ? 60_000 : false, refetchOnWindowFocus: true,
    });
    useEffect(() => {
        if (!user || !state.enabled || !counts.data) return;
        void readPushBinding().then(binding => {
            if (binding?.enabled && binding.user_id === user.id) return setBadge(inboxTotal(counts.data.data));
        }).catch(() => {});
    }, [counts.data, user?.id, state.enabled]);
    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;
        const refresh = (event: MessageEvent) => {
            if (event.data?.type === "PM_PUSH_RECEIVED") void queries.invalidateQueries({ queryKey: ["inbox"] });
        };
        navigator.serviceWorker.addEventListener("message", refresh);
        return () => navigator.serviceWorker.removeEventListener("message", refresh);
    }, [queries]);
    return null;
}
